import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { esErrorInteraccionMsal, recuperarSesionMsal } from '@core/auth/recuperacion-sesion';
import { takeUntil, catchError, switchMap } from 'rxjs/operators';
import { EMPTY, Subject, from } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ToastService, TOAST_SEVERITY, TOAST_PERSISTE_EN_NAVEGACION } from '@shared/servicios/toast.service';
import { ContextoService } from '@core/services/contexto.service';
import { AuthService } from '@core/services/auth.service';
import { InactividadService } from '@core/services/inactividad.service';
import { MENSAJE_SESION_CERRADA_INACTIVIDAD } from '@core/constants/inactividad.constants';
import { IndicadorCargaComponent } from './shared/componentes/indicador-carga/indicador-carga.component';
import { ModalSesionInactivaComponent } from '@shared/componentes/modal-sesion-inactiva/modal-sesion-inactiva.component';
import { CargaGlobalService } from '@shared/servicios/carga-global.service';
import { GestorSesionUsuarioService } from '@core/services/gestor-sesion-usuario.service';
import { PermisosService } from '@core/services/permisos.service';
import { RegistroIngresoService } from '@core/services/registro-ingreso.service';
import { esRolAdmin } from '@core/constants/roles.constants';

const EVENTOS_FALLO_AUTENTICACION: ReadonlySet<EventType> = new Set([
  EventType.LOGIN_FAILURE,
  EventType.ACQUIRE_TOKEN_FAILURE,
  EventType.SSO_SILENT_FAILURE
]);

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ToastModule,
    DynamicDialogModule,
    ConfirmDialog,
    IndicadorCargaComponent,
    ModalSesionInactivaComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly cargaGlobal;
  private readonly _destroying$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly inactividadService = inject(InactividadService);
  private readonly registroIngresoService = inject(RegistroIngresoService);
  private readonly msalService = inject(MsalService);

  constructor(
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly toastService: ToastService,
    private readonly contextoService: ContextoService,
    private readonly cargaGlobalService: CargaGlobalService,
    private readonly gestorSesionService: GestorSesionUsuarioService,
    private readonly permisosService: PermisosService
  ) {
    this.cargaGlobal = this.cargaGlobalService.cargando;
  }

  public ngOnInit(): void {
    this.permisosService.inicializarEscuchaMsal();
    this.notificarCierrePorInactividad();

    if (this.authService.estaAutenticado()) {
      this.inactividadService.iniciarVigilancia();
    }

    this.permisosService.contextoCambiado$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        const contextoActivo = this.contextoService.obtenerContextoActivo();
        if (contextoActivo?.modo && !contextoActivo?.permissions?.length) {
          this.cargarPermisosUsuario();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this.inactividadService.iniciarVigilancia();
          this.registrarIngreso();
          this.onLoginSuccess();
        } else if (result.eventType === EventType.LOGOUT_SUCCESS) {
          this.inactividadService.detenerVigilancia();
          this.gestorSesionService.limpiarSesion();
        } else if (EVENTOS_FALLO_AUTENTICACION.has(result.eventType)) {
          this.manejarFalloAutenticacion(result.error);
        }
      });
  }

  public ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  public onToastCerrado(event: { message: { id?: string } }): void {
    if (event?.message?.id) {
      this.toastService.marcarComoCerrado(event.message.id);
    }
  }

  private manejarFalloAutenticacion(error: EventMessage['error']): void {
    if (!esErrorInteraccionMsal(error)) {
      return;
    }

    recuperarSesionMsal(() => {
      void this.msalService.instance.loginRedirect();
    });
  }

  private onLoginSuccess(): void {
    this.navegarSegunPerfil();
  }

  private registrarIngreso(): void {
    this.registroIngresoService.registrarIngreso()
      .pipe(
        switchMap(() => from(this.authService.forzarRefrescoToken())),
        takeUntil(this._destroying$),
        catchError(() => EMPTY)
      )
      .subscribe();
  }

  private navegarSegunPerfil(): void {
    const orgRoles = this.contextoService.obtenerOrgRolesJWT() || [];
    const esAdmin = orgRoles.some(or => esRolAdmin(or.role));

    if (esAdmin) {
      void this.router.navigate(['/seleccion-modo']);
    } else {
      void this.router.navigate(['/perfiles-empresas']);
    }
  }

  private notificarCierrePorInactividad(): void {
    if (this.inactividadService.consumirCierrePorInactividad()) {
      this.toastService.mostrar(MENSAJE_SESION_CERRADA_INACTIVIDAD, TOAST_SEVERITY.warn, undefined, TOAST_PERSISTE_EN_NAVEGACION);
    }
  }

  private cargarPermisosUsuario(): void {
    this.gestorSesionService.cargarPermisosUsuario().pipe(
      catchError(() => EMPTY)
    ).subscribe();
  }
}
